import type { JSX, ReactNode } from 'react';

import type { State } from '../state-machine/types';

/**
 * Declarative view switch for a state machine: renders the branch whose key
 * equals the current `state.value`, or `fallback` when none match.
 *
 * The `views` keys are constrained to the machine's state-name union, so a
 * misspelled key is a type error. A state you don't list is not — it falls
 * through to `fallback` (or renders nothing), so keep a `fallback` unless every
 * phase is listed. Branches are render functions, so only the current phase's
 * subtree is constructed.
 *
 * ```tsx
 * <Match
 *   state={state}
 *   views={{
 *     done: () => <Success />,
 *     failed: () => <Failure />,
 *   }}
 *   fallback={() => <Loader />} // any in-flight phase
 * />
 * ```
 */
export type MatchProps<TContext, TValue extends string> = {
  state: State<TContext, TValue>;
  /** Map of machine state value → the view for that phase. */
  views: Partial<Record<TValue, () => ReactNode>>;
  /** Rendered when no key in `views` matches `state.value`. */
  fallback?: () => ReactNode;
};

export function Match<TContext, TValue extends string>({
  state,
  views,
  fallback,
}: MatchProps<TContext, TValue>): JSX.Element {
  const render = views[state.value] ?? fallback;
  return <>{render ? render() : null}</>;
}
